
import { Options, Vue } from 'vue-class-component'
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import IconRfid from '@/components/svg/IconRfid.vue'
import IconArrowBack from '@/components/svg/IconArrowBack.vue'
import RfidItemSelector from '@/views/bizBrowser/RfidItemSelector.vue'
import { RECYCLE_MULTIPLE_BARCODE, SAVE_SCANNED_BARCODES } from '@/store/actions'
import { IBarcode, IBarcodeDefinitionType, IProject, TError } from 'smartbarcode-web-core/src/utils/types/index'
import { recycleMultipleBarcode, getBarcode } from '@/utils/api'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { RfidScanParsedResult } from '@/utils/bizBrowser/rfidScanResultHandler'
import errorHandler from '@/utils/errorHandler'
import { openMessage } from '@/utils/helpers'

@Options({
  components: {
    BarcodeLabelList,
    IconRfid,
    IconArrowBack,
    RfidItemSelector,
  },
  name: 'BulkRecycleRfid',
})
export default class BulkRecycleRfid extends Vue {
  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  scannedBarcodeList: IBarcode[] = []
  showRfidScanDialog = true

  get barcode(): IBarcode {
    return this.$store.state.barcode.barcode
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  get childrenCount() {
    return this.barcode?.childrenCount || 0
  }

  get parentBarcodeId() {
    return this.$store.state.barcode?.barcode?.id || ''
  }

  created() {
    // Initialize with the first barcode if needed
    if (this.$store.state.barcode.barcode) {
      // No need to add the initial barcode for recycle like tracking does
    }
  }

  goBack() {
    this.$router.back()
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (to.name === 'scanned-barcode-list') {
      next()
      return
    }

    if (this.scannedBarcodeList.length > 0) {
      try {
        await this.$confirm(this.$t('confirm_rfid_scan_result_is_not_saved_when_leaving_page'), this.$t('warning'), {
          confirmButtonText: this.$t('ok'),
          cancelButtonText: this.$t('cancel'),
          type: 'warning',
        })
        next()
      } catch {
        next(false)
      }
    } else {
      next()
    }
  }

  barcodeTypeInfo(barcodeType: string): IBarcodeDefinitionType {
    return this.$store.state.project?.details?.barcodeTypes[barcodeType]
  }

  isCurrentRecycleableTrackpoint(barcode: IBarcode): boolean {
    return (this.barcodeTypeInfo(barcode.barcodeType)?.barcodeRecyclableTrackPoints ?? []).includes(
      this.currentTrackPointKey
    )
  }

  hasParent(barcode: IBarcode): boolean {
    return barcode?.hasParent
  }

  isAllowRecycle(barcode: IBarcode): { message: string } {
    const error = { message: '' }
    if (!this.isCurrentRecycleableTrackpoint(barcode)) {
      error.message = 'cant_recycle_at_this_trackpoint'
    }

    if (this.childrenCount > 0) {
      if (!this.project.isRecycleChildren || this.hasParent(barcode)) {
        error.message = 'errors.3025'
      }
    }

    if (this.parentBarcodeId === barcode?.id) {
      error.message = 'barcode_pairing_by_itself_error'
    }

    if (!barcode?.barcodeType) {
      error.message = 'barcode not activated'
    }
    return error
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  async validateBarcode(resultScanned: IBarcode) {
    const error = this.isAllowRecycle(resultScanned)
    if (error.message) {
      throw this.$t(error.message)
    }
    const data = {
      barcodeIds: [resultScanned?.id],
      isDryRun: true,
    }
    await recycleMultipleBarcode(data)
  }

  async onRfidScanConfirmed(rfidList: RfidScanParsedResult[]) {
    this.loading = true
    try {
      const projectCode = this.$route?.params?.project?.toString().split('@')[0] || ''

      for (const rfidResult of rfidList) {
        try {
          const barcodeId = `projectCode=${projectCode}`
          const scannedBarcode = await getBarcode(barcodeId, rfidResult.parsedValue)

          const isAlreadyAdded = this.scannedBarcodeList.find((barcode: IBarcode) => barcode.id === scannedBarcode.id)
          if (isAlreadyAdded) {
            continue
          }

          await this.validateBarcode(scannedBarcode)
          this.scannedBarcodeList.push(scannedBarcode)
          openMessage(this.$t('barcode detected'), 'success')
        } catch (error) {
          if (error !== 'cancel') {
            errorHandler(error as TError)
          }
        }
      }
    } finally {
      this.loading = false
    }
  }

  async submit() {
    if (!this.scannedBarcodeList || this.scannedBarcodeList.length === 0) {
      return
    }
    const listBarcodeIds = [
      this.$route.params.barcodeId,
      ...this.scannedBarcodeList.map((barcode: IBarcode) => barcode.id),
    ]
    this.$store.dispatch(SAVE_SCANNED_BARCODES, this.scannedBarcodeList)
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('recycle_read_result'),
        operation: RECYCLE_MULTIPLE_BARCODE,
        extParams: JSON.stringify(listBarcodeIds),
      },
    })
  }
}
